import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Circle,
  Sparkles,
  PartyPopper,
  RotateCcw,
  BookOpen,
  Calendar,
  BarChart3,
  RefreshCw,
  Users,
  ArrowRight,
  Tags,
  Clock,
  Building2,
  TrendingUp,
  Brain,
  AlertTriangle,
  Lightbulb,
  Pause,
  Play,
  CheckSquare,
  DollarSign,
  Star,
  Timer,
  FileWarning,
  Calculator,
  UserX,
  Target,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ChecklistItem } from '@/types/onboarding';
import { useChecklistStore } from '@/stores/checklistStore';
import { usePlanningStore } from '@/stores/planningStore';
import { usePilotageStore } from '@/stores/pilotageStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Interface pour un élément du guide (sous-section)
 */
interface GuideItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Interface pour une section principale du guide
 */
interface GuideSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  badge?: {
    label: string;
    variant: 'default' | 'destructive' | 'secondary' | 'outline';
    className?: string;
  };
  items: GuideItem[];
}

/**
 * Configuration détaillée d'un statut de chantier
 */
interface StatutDetail {
  id: string;
  nom: string;
  emoji: string;
  couleur: string;
  resume: string;
  details: string[];
  transitions: string[];
  automatismes?: string[];
  modal?: string;
}

/**
 * Définition complète de tous les statuts avec leurs comportements
 */
const STATUTS_DETAILS: StatutDetail[] = [
  {
    id: 'brouillon',
    nom: 'Brouillon',
    emoji: '📝',
    couleur: 'text-gray-500',
    resume: 'Chantier en préparation, non comptabilisé dans les analyses.',
    details: [
      'Permet de préparer un chantier sans impacter les statistiques',
      'Idéal pour les projets en phase de réflexion ou négociation',
      'N\'apparaît pas dans les rapports de rentabilité',
      'Aucune alerte générée pour ce statut',
    ],
    transitions: [
      '→ Devis envoyé : quand le devis est prêt à être envoyé',
      '→ Planifié : si le projet est validé sans phase devis',
      '→ En cours : démarrage immédiat des travaux',
      '→ Annulé : abandon du projet',
    ],
  },
  {
    id: 'devis_envoye',
    nom: 'Devis envoyé',
    emoji: '📊',
    couleur: 'text-purple-500',
    resume: 'En attente de validation client, suivi commercial actif.',
    details: [
      'Le devis a été transmis au client et attend une réponse',
      'Permet de suivre les opportunités en cours',
      'Visible dans le pipeline commercial',
      'Peut être modifié pour renégociation si nécessaire',
    ],
    transitions: [
      '→ Brouillon : pour modifier le devis avant renvoi',
      '→ Planifié : le client a accepté le devis',
      '→ En cours : démarrage immédiat après acceptation',
      '→ Annulé : le client a refusé le devis',
    ],
  },
  {
    id: 'planifie',
    nom: 'Planifié',
    emoji: '📅',
    couleur: 'text-blue-500',
    resume: 'Validé et programmé, passera automatiquement en cours à la date de début.',
    details: [
      'Le chantier est confirmé et a une date de début définie',
      'Les ressources peuvent être réservées à l\'avance',
      'Visible dans le planning prévisionnel',
      'Nécessite une date de début pour être validé',
    ],
    transitions: [
      '→ Brouillon : annuler la planification',
      '→ Devis envoyé : renégociation nécessaire',
      '→ En cours : démarrer les travaux manuellement',
      '→ Annulé : annuler le chantier',
    ],
    automatismes: [
      '🔄 AUTOMATIQUE : Passe en "En cours" à la date de début prévue',
      '📅 Le changement se fait automatiquement le jour J',
      '⚡ Aucune action manuelle requise au démarrage',
    ],
  },
  {
    id: 'en_cours',
    nom: 'En cours',
    emoji: '🏗️',
    couleur: 'text-green-500',
    resume: 'Travaux actifs, suivi des coûts et rentabilité en temps réel.',
    details: [
      'Les travaux sont en cours d\'exécution',
      'Toutes les dépenses sont comptabilisées en temps réel',
      'La rentabilité est calculée et mise à jour automatiquement',
      'Les alertes de dépassement sont actives',
    ],
    transitions: [
      '→ Suspendu : mettre en pause temporaire',
      '→ Terminé : clôturer le chantier',
      '→ Annulé : abandonner le chantier',
    ],
    modal: '⚠️ IMPORTANT : Toutes les transitions depuis "En cours" passent par un modal de confirmation pour documenter la raison du changement.',
  },
  {
    id: 'suspendu',
    nom: 'Suspendu',
    emoji: '⏸️',
    couleur: 'text-orange-500',
    resume: 'Pause temporaire documentée, en attente de reprise.',
    details: [
      'Arrêt temporaire des travaux (intempéries, attente matériel...)',
      'La raison de suspension est enregistrée',
      'Les coûts fixes continuent d\'être suivis',
      'Le chantier reste visible dans les tableaux de bord',
    ],
    transitions: [
      '→ En cours : reprendre les travaux',
      '→ Terminé : clôturer avec travaux partiels',
      '→ Annulé : abandonner définitivement',
    ],
    modal: '📋 Un modal de suspension permet de documenter : date prévue de reprise, motif de suspension, notes complémentaires.',
    automatismes: [
      '💡 Lors de la reprise, un modal permet de confirmer la date effective',
      '📊 La durée de suspension est tracée pour analyse',
    ],
  },
  {
    id: 'termine',
    nom: 'Terminé',
    emoji: '✅',
    couleur: 'text-emerald-500',
    resume: 'Travaux finis, analyse finale de rentabilité disponible.',
    details: [
      'Les travaux sont terminés et le chantier est clôturé',
      'La rentabilité finale est calculée',
      'Le bilan complet est disponible dans les rapports',
      'Peut être archivé pour libérer la liste active',
    ],
    transitions: [
      '→ En cours : rouvrir si travaux supplémentaires nécessaires',
    ],
    modal: '🔓 Un modal de réouverture demande la raison (travaux supplémentaires, reprises, etc.).',
    automatismes: [
      '📦 Peut être archivé après analyse finale',
      '📊 Contribue aux statistiques globales de l\'entreprise',
    ],
  },
  {
    id: 'annule',
    nom: 'Annulé',
    emoji: '❌',
    couleur: 'text-red-500',
    resume: 'Projet abandonné, conservé dans l\'historique.',
    details: [
      'Le chantier a été annulé et ne sera pas réalisé',
      'La raison d\'annulation est documentée',
      'Reste accessible pour consultation et analyse',
      'N\'impacte pas les statistiques de rentabilité',
    ],
    transitions: [
      '→ Planifié : réactiver si le client revient',
      '→ En cours : reprendre immédiatement les travaux',
    ],
    modal: '🔄 Un modal de réactivation permet de documenter pourquoi le projet reprend.',
  },
  {
    id: 'archive',
    nom: 'Archivé',
    emoji: '📦',
    couleur: 'text-slate-500',
    resume: 'Masqué des listes principales, accessible via les filtres.',
    details: [
      'Le chantier est masqué de la vue par défaut',
      'Reste consultable en activant le filtre "Archivés"',
      'Utile pour garder les listes actives propres',
      'Seuls les brouillons, terminés ou annulés peuvent être archivés',
    ],
    transitions: [
      '🔒 Aucune transition directe depuis archivé',
      '↩️ Utiliser l\'action "Désarchiver" pour restaurer',
    ],
    automatismes: [
      '💡 Le désarchivage permet de choisir le statut de retour (Brouillon ou Planifié)',
    ],
  },
];

interface ChecklistPanelExpandedProps {
  isVisible: boolean;
  onCollapse: () => void;
  items: ChecklistItem[];
  completedCount: number;
  totalCount: number;
  progressPercent: number;
  isAllCompleted: boolean;
  onHideChecklist: () => void;
  onHideCompletely?: () => void;
  onResetChecklist?: () => void;
}

/**
 * Panneau déplié de la Checklist "Premiers pas" / Guide d'utilisation
 * Overlay centré avec les 8 étapes cliquables ou le guide d'utilisation avec accordéons
 */
const ChecklistPanelExpanded = ({
  isVisible,
  onCollapse,
  items,
  completedCount,
  totalCount,
  progressPercent,
  isAllCompleted,
  onHideChecklist,
  onHideCompletely,
  onResetChecklist,
}: ChecklistPanelExpandedProps) => {
  const navigate = useNavigate();
  const { lastCreatedChantierId, setLastCreatedChantierId } = useChecklistStore();
  const { openPanel: openPlanning, setActiveTab } = usePlanningStore();
  const { openPanel: openPilotage } = usePilotageStore();
  const [expandedSection, setExpandedSection] = useState<string>('');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [expandedStatut, setExpandedStatut] = useState<string | null>(null);
  const [isValidatingChantier, setIsValidatingChantier] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);

  // Suivi des items ayant déjà joué l'animation étoile filante (persiste entre re-renders)
  const animatedItemsRef = useRef<Set<string>>(new Set());
  // ID de l'item actuellement en train de jouer l'animation étoile filante
  const [shootingStarItemId, setShootingStarItemId] = useState<string | null>(null);

  const nextIncompleteItemId = useMemo(() => {
    const nextItem = items.find(item => !item.isCompleted);
    return nextItem?.id || null;
  }, [items]);

  useEffect(() => {
    if (isVisible && !isAllCompleted && nextIncompleteItemId) {
      setIsBlinking(true);
      const timer = setTimeout(() => setIsBlinking(false), 10000);
      return () => clearTimeout(timer);
    } else {
      setIsBlinking(false);
    }
  }, [isVisible, isAllCompleted, nextIncompleteItemId]);

  // Animation étoile filante : se déclenche UNE SEULE FOIS par item quand il devient le prochain step
  useEffect(() => {
    if (
      isVisible &&
      !isAllCompleted &&
      nextIncompleteItemId &&
      !animatedItemsRef.current.has(nextIncompleteItemId)
    ) {
      animatedItemsRef.current.add(nextIncompleteItemId);
      setShootingStarItemId(nextIncompleteItemId);
      const timer = setTimeout(() => setShootingStarItemId(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isAllCompleted, nextIncompleteItemId]);

  /**
   * Configuration des 6 sections du guide avec leurs éléments
   */
  const guideSections: GuideSection[] = useMemo(() => [
    // SECTION 1 : CHANTIERS
    {
      id: 'chantiers',
      title: 'Chantiers',
      icon: <Building2 className="h-5 w-5" />,
      iconColor: 'text-blue-500',
      items: [
        {
          id: 'etats-chantier',
          title: 'Les états d\'un chantier',
          icon: <Tags className="h-4 w-4 text-blue-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Chaque chantier passe par différents états qui reflètent son avancement.
                Ces états permettent de suivre précisément où en est chaque projet.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Cycle de vie principal</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 p-2 rounded bg-gray-500/10">
                    <span className="text-base">📝</span>
                    <div className="flex-1">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Brouillon</span>
                      <p className="text-[10px] text-muted-foreground">Projet en préparation, non comptabilisé</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded bg-purple-500/10">
                    <span className="text-base">📊</span>
                    <div className="flex-1">
                      <span className="text-xs font-medium text-purple-600 dark:text-purple-400">Devis envoyé</span>
                      <p className="text-[10px] text-muted-foreground">En attente de réponse client</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded bg-blue-500/10">
                    <span className="text-base">📅</span>
                    <div className="flex-1">
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Planifié</span>
                      <p className="text-[10px] text-muted-foreground">Validé, démarre automatiquement à la date prévue</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded bg-green-500/10">
                    <span className="text-base">🏗️</span>
                    <div className="flex-1">
                      <span className="text-xs font-medium text-green-600 dark:text-green-400">En cours</span>
                      <p className="text-[10px] text-muted-foreground">Travaux actifs, suivi temps réel</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded bg-emerald-500/10">
                    <span className="text-base">✅</span>
                    <div className="flex-1">
                      <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Terminé</span>
                      <p className="text-[10px] text-muted-foreground">Travaux finis, bilan disponible</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-blue-500/10 border border-blue-500/20">
                <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 mb-1">💡 Comment changer l'état ?</p>
                <ol className="text-[10px] text-blue-600/80 dark:text-blue-400/80 space-y-0.5">
                  <li>1. Ouvrez la fiche du chantier</li>
                  <li>2. Cliquez sur le badge d'état en haut</li>
                  <li>3. Sélectionnez le nouvel état</li>
                  <li>4. Confirmez (un motif peut être demandé)</li>
                </ol>
              </div>

              <p className="text-[10px] text-muted-foreground/80 italic">
                États spéciaux : Suspendu (pause temporaire), Annulé (abandon), Archivé (masqué des listes)
              </p>
            </div>
          ),
        },
        {
          id: 'statuts-chantier',
          title: 'Les Statuts de Chantier',
          icon: <Tags className="h-4 w-4 text-purple-400" />,
          content: (
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {STATUTS_DETAILS.map((statut) => (
                <div key={statut.id} className="border border-border/50 rounded-lg overflow-hidden bg-muted/20">
                  <button
                    onClick={() => setExpandedStatut(expandedStatut === statut.id ? null : statut.id)}
                    className="w-full flex items-center gap-2 p-2 hover:bg-muted/50 transition-colors"
                  >
                    <span className="text-lg">{statut.emoji}</span>
                    <span className={cn("text-sm font-semibold flex-1 text-left", statut.couleur)}>{statut.nom}</span>
                    <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", expandedStatut === statut.id && "rotate-180")} />
                  </button>
                  {expandedStatut === statut.id && (
                    <div className="px-3 pb-3 pt-1 border-t border-border/30 space-y-2">
                      <p className="text-xs text-foreground font-medium">{statut.resume}</p>
                      <div>
                        <p className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">Caractéristiques</p>
                        <ul className="space-y-1">
                          {statut.details.map((detail, i) => (
                            <li key={i} className="text-xs text-muted-foreground flex gap-1.5">
                              <span className="text-primary">•</span><span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {statut.automatismes && (
                        <div className="p-2 rounded-md bg-blue-500/10 border border-blue-500/20">
                          <p className="text-[10px] uppercase font-semibold text-blue-500 mb-1">Automatismes</p>
                          <ul className="space-y-1">
                            {statut.automatismes.map((auto, i) => (
                              <li key={i} className="text-xs text-blue-600 dark:text-blue-400">{auto}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {statut.modal && (
                        <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                          <p className="text-xs text-amber-600 dark:text-amber-400">{statut.modal}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ),
          action: { label: 'Voir mes chantiers', onClick: () => { onCollapse(); navigate('/projects'); } },
        },
        {
          id: 'suspendre-chantier',
          title: 'Suspendre un chantier',
          icon: <Pause className="h-4 w-4 text-orange-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                La suspension permet de mettre un chantier en pause temporaire tout en gardant
                un historique documenté. Utilisez-la quand les travaux doivent s'arrêter
                temporairement mais que le projet n'est pas abandonné.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment suspendre ?</p>
                <ol className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">1</span>
                    <span>Ouvrez la fiche du chantier "En cours"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">2</span>
                    <span>Cliquez sur le badge d'état (🏗️ En cours)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">3</span>
                    <span>Sélectionnez "⏸️ Suspendu"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">4</span>
                    <span>Dans le modal, renseignez le <strong>motif</strong> et la <strong>date de reprise prévue</strong></span>
                  </li>
                </ol>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Motifs courants</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">🌧️ Intempéries</div>
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">📦 Attente matériaux</div>
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">👤 Problème client</div>
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">📋 Administratif</div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-orange-500/10 border border-orange-500/20">
                <p className="text-xs font-medium text-orange-600 dark:text-orange-400 mb-1">⚠️ Ce qui se passe</p>
                <ul className="text-[10px] text-orange-600/80 dark:text-orange-400/80 space-y-0.5">
                  <li>• Les affectations sont maintenues (équipe reste liée)</li>
                  <li>• La durée de suspension est tracée pour analyse</li>
                  <li>• Le chantier reste visible dans le tableau de bord</li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          id: 'reprendre-chantier',
          title: 'Reprendre un chantier suspendu',
          icon: <Play className="h-4 w-4 text-green-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Quand les conditions sont réunies pour reprendre les travaux, vous pouvez
                remettre le chantier "En cours". B8ild garde trace de la durée de suspension
                pour vos analyses de rentabilité.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment reprendre ?</p>
                <ol className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">1</span>
                    <span>Ouvrez la fiche du chantier "Suspendu"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">2</span>
                    <span>Cliquez sur le badge d'état (⏸️ Suspendu)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">3</span>
                    <span>Sélectionnez "🏗️ En cours"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">4</span>
                    <span>Dans le modal, confirmez la date de reprise effective</span>
                  </li>
                </ol>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Options disponibles</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 p-1.5 rounded bg-green-500/10">
                    <span className="text-green-400">▶️</span>
                    <div>
                      <span className="text-xs font-medium text-green-600 dark:text-green-400">Reprendre aujourd'hui</span>
                      <p className="text-[10px] text-muted-foreground">Travaux reprennent immédiatement</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-blue-500/10">
                    <span className="text-blue-400">📅</span>
                    <div>
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Ajuster la date de fin</span>
                      <p className="text-[10px] text-muted-foreground">Décaler la fin selon la durée de pause</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-green-500/10 border border-green-500/20">
                <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">📊 Suivi automatique</p>
                <ul className="text-[10px] text-green-600/80 dark:text-green-400/80 space-y-0.5">
                  <li>• Durée de suspension enregistrée dans l'historique</li>
                  <li>• Affectations réactivées automatiquement</li>
                  <li>• Calcul de rentabilité reprend en temps réel</li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          id: 'cloturer-chantier',
          title: 'Clôturer un chantier',
          icon: <CheckSquare className="h-4 w-4 text-emerald-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                La clôture marque la fin officielle des travaux. C'est le moment de finaliser
                toutes les données financières et d'obtenir le bilan complet du chantier.
                Prenez le temps de bien renseigner les informations pour un suivi optimal.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment clôturer ?</p>
                <ol className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">1</span>
                    <span>Ouvrez la fiche du chantier "En cours"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">2</span>
                    <span>Cliquez sur le badge d'état (🏗️ En cours)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">3</span>
                    <span>Sélectionnez "✅ Terminé"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">4</span>
                    <span>Suivez l'assistant de clôture</span>
                  </li>
                </ol>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">L'assistant de clôture</p>
                <div className="space-y-1">
                  <div className="p-1.5 rounded bg-muted/30">
                    <div className="flex items-center gap-2">
                      <span className="text-xs">📊</span>
                      <span className="text-xs font-medium text-foreground">Vérification financière</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground ml-5">Factures payées ? Frais ajoutés ?</p>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <div className="flex items-center gap-2">
                      <span className="text-xs">⏰</span>
                      <span className="text-xs font-medium text-foreground">Heures supplémentaires</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground ml-5">Ajoutez les heures sup non déclarées</p>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <div className="flex items-center gap-2">
                      <span className="text-xs">💬</span>
                      <span className="text-xs font-medium text-foreground">Commentaire de clôture</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground ml-5">Notes sur le déroulement du chantier</p>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <div className="flex items-center gap-2">
                      <span className="text-xs">📄</span>
                      <span className="text-xs font-medium text-foreground">Bilan PDF</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground ml-5">Téléchargez le rapport complet</p>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mb-1">🎁 Bonus clôture</p>
                <ul className="text-[10px] text-emerald-600/80 dark:text-emerald-400/80 space-y-0.5">
                  <li>• <strong>Analyse IA gratuite</strong> (ne compte pas dans le quota)</li>
                  <li>• Bilan PDF avec toutes les statistiques</li>
                  <li>• Possibilité de rouvrir si nécessaire</li>
                </ul>
              </div>
            </div>
          ),
        },
      ],
    },

    // SECTION 2 : ÉQUIPE & PLANNING
    {
      id: 'equipe-planning',
      title: 'Équipe & Planning',
      icon: <Users className="h-5 w-5" />,
      iconColor: 'text-green-500',
      items: [
        {
          id: 'planning',
          title: 'Le Planning',
          icon: <Calendar className="h-4 w-4 text-blue-400" />,
          content: (
            <ol className="space-y-2 text-xs text-muted-foreground">
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">1</span>Cliquez sur "Planning" à droite</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">2</span>Visualisez les affectations</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">3</span>Cliquez sur une date</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">4</span>Utilisez "+ Affectation"</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">5</span>Vue semaine/mois</li>
            </ol>
          ),
          action: { label: 'Ouvrir le Planning', onClick: () => { onCollapse(); openPlanning(); setActiveTab('planning'); } },
        },
        {
          id: 'rotation-planning',
          title: 'Rotation depuis le Planning',
          icon: <RefreshCw className="h-4 w-4 text-purple-400" />,
          content: (
            <ol className="space-y-2 text-xs text-muted-foreground">
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">1</span>Onglet "Rotation"</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">2</span>Sélectionnez un membre</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">3</span>Choisissez le nouveau chantier</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">4</span>Définissez les dates</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">5</span>Ancien chantier libéré auto</li>
            </ol>
          ),
          action: { label: 'Accéder aux Rotations', onClick: () => { onCollapse(); openPlanning(); setActiveTab('rotation'); } },
        },
        {
          id: 'rotation-equipe',
          title: 'Rotation depuis la page Équipe',
          icon: <Users className="h-4 w-4 text-green-400" />,
          content: (
            <ol className="space-y-2 text-xs text-muted-foreground">
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">1</span>Page "Équipe"</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">2</span>Cliquez sur le statut</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">3</span>Bouton "Rotation"</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">4</span>Définissez le statut</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">5</span>MAJ automatique</li>
            </ol>
          ),
          action: { label: 'Voir la page Équipe', onClick: () => { onCollapse(); navigate('/team'); } },
        },
        {
          id: 'cout-journalier',
          title: 'Comprendre le coût journalier',
          icon: <DollarSign className="h-4 w-4 text-amber-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Formule :</p>
              <div className="p-3 rounded-lg bg-muted/50 border border-border/50">
                <p className="text-sm font-mono text-center text-foreground">Taux horaire × 8h × (1 + Charges%)</p>
              </div>
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Exemple</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 rounded bg-muted/30"><span className="text-muted-foreground">Taux:</span><span className="font-medium text-foreground ml-1">18€/h</span></div>
                  <div className="p-2 rounded bg-muted/30"><span className="text-muted-foreground">Charges:</span><span className="font-medium text-foreground ml-1">45%</span></div>
                </div>
                <div className="p-2 rounded bg-primary/10 border border-primary/20 text-center">
                  <span className="text-xs text-muted-foreground">= </span>
                  <span className="text-sm font-semibold text-primary">208,80 €/j</span>
                </div>
              </div>
            </div>
          ),
        },
        {
          id: 'statuts-membres',
          title: 'Les statuts des membres',
          icon: <Users className="h-4 w-4 text-cyan-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Chaque membre de votre équipe a un statut qui détermine sa disponibilité.
                Ce statut affecte les calculs de rentabilité et le planning.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Les 4 statuts</p>
                <div className="space-y-1.5">
                  <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                      <span className="text-xs font-semibold text-green-600 dark:text-green-400">Sur chantier</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 ml-4">
                      Le membre est actuellement affecté à un chantier. Son coût journalier
                      est comptabilisé dans les dépenses du chantier.
                    </p>
                  </div>

                  <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                      <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">Disponible</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 ml-4">
                      Le membre n'est affecté à aucun chantier. Il peut être assigné
                      à un nouveau projet depuis le planning.
                    </p>
                  </div>

                  <div className="p-2 rounded bg-gray-500/10 border border-gray-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-gray-500"></span>
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Repos</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 ml-4">
                      Jour de congé, week-end, ou jour férié. Le membre n'est pas
                      disponible mais reprendra normalement après.
                    </p>
                  </div>

                  <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                      <span className="text-xs font-semibold text-red-600 dark:text-red-400">En arrêt</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 ml-4">
                      Arrêt maladie ou accident. Le membre est indisponible pour
                      une durée indéterminée ou définie.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-blue-500/10 border border-blue-500/20">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">💡 Comment changer le statut ?</p>
                <ol className="text-[10px] text-blue-600/80 dark:text-blue-400/80 space-y-0.5">
                  <li>1. Allez sur la page "Équipe"</li>
                  <li>2. Cliquez sur le badge de statut du membre</li>
                  <li>3. Sélectionnez le nouveau statut</li>
                </ol>
              </div>
            </div>
          ),
        },
        {
          id: 'absences',
          title: 'Gérer les absences',
          icon: <UserX className="h-4 w-4 text-red-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Déclarez les absences de vos membres pour maintenir un planning précis.
                B8ild ajuste automatiquement les affectations et calculs de coûts.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment déclarer une absence ?</p>
                <ol className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">1</span>
                    <span>Allez sur la page "Équipe"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">2</span>
                    <span>Trouvez le membre concerné</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">3</span>
                    <span>Cliquez sur son badge de statut</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">4</span>
                    <span>Choisissez "Repos" ou "En arrêt"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">5</span>
                    <span>Renseignez les <strong>dates de début et fin</strong></span>
                  </li>
                </ol>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Types d'absences</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="p-1.5 rounded bg-gray-500/10 border border-gray-500/20">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">🏖️ Repos</span>
                    <p className="text-[10px] text-muted-foreground">Congés, RTT, récup</p>
                  </div>
                  <div className="p-1.5 rounded bg-red-500/10 border border-red-500/20">
                    <span className="text-xs font-medium text-red-600 dark:text-red-400">🏥 Arrêt</span>
                    <p className="text-[10px] text-muted-foreground">Maladie, accident</p>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">⚡ Ce qui se passe automatiquement</p>
                <ul className="text-[10px] text-amber-600/80 dark:text-amber-400/80 space-y-0.5">
                  <li>• Les affectations pendant l'absence sont suspendues</li>
                  <li>• Le coût n'est pas comptabilisé sur les chantiers</li>
                  <li>• Le planning se met à jour en temps réel</li>
                  <li>• À la fin de l'absence, le membre redevient disponible</li>
                </ul>
              </div>
            </div>
          ),
          action: { label: 'Gérer l\'équipe', onClick: () => { onCollapse(); navigate('/team'); } },
        },
        {
          id: 'heures-supplementaires',
          title: 'Les Heures Supplémentaires',
          icon: <Clock className="h-4 w-4 text-amber-400" />,
          content: (
            <div className="space-y-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-600 dark:text-amber-400"><strong>💡 Optionnel</strong> — Activez dans paramètres.</p>
              </div>
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Majorations</p>
                <div className="grid grid-cols-1 gap-1.5">
                  <div className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/50">
                    <span className="text-muted-foreground">36-43h/sem</span><span className="font-semibold text-blue-500">+25%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/50">
                    <span className="text-muted-foreground">44h+/sem</span><span className="font-semibold text-blue-500">+50%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/50">
                    <span className="text-muted-foreground">Nuit/Férié</span><span className="font-semibold text-purple-500">+100%</span>
                  </div>
                </div>
              </div>
            </div>
          ),
          action: { label: 'Paramètres entreprise', onClick: () => { onCollapse(); navigate('/settings'); } },
        },
      ],
    },

    // SECTION 3 : RENTABILITÉ
    {
      id: 'rentabilite',
      title: 'Rentabilité',
      icon: <TrendingUp className="h-5 w-5" />,
      iconColor: 'text-amber-500',
      badge: { label: 'Important', variant: 'secondary', className: 'bg-amber-500/20 text-amber-600 dark:text-amber-400' },
      items: [
        {
          id: 'pilotage',
          title: 'Centre de Pilotage',
          icon: <BarChart3 className="h-4 w-4 text-orange-400" />,
          content: (
            <ol className="space-y-2 text-xs text-muted-foreground">
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">1</span>Onglet "Pilotage" à droite</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">2</span>Alertes dépassement budget</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">3</span>Retards de paiement</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">4</span>Chantiers sans équipe</li>
              <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">5</span>Actions rapides</li>
            </ol>
          ),
          action: { label: 'Ouvrir le Pilotage', onClick: () => { onCollapse(); openPilotage(); } },
        },
        {
          id: 'badges-rentabilite',
          title: 'Lire les badges de rentabilité',
          icon: <Target className="h-4 w-4 text-emerald-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Chaque chantier affiche un badge coloré qui indique sa rentabilité actuelle.
                Ce badge est calculé automatiquement à partir de vos données financières.
              </p>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Les 4 niveaux</p>

                <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🟢</span>
                    <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Excellent</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">&gt;35%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 ml-7">
                    Marge confortable. Le chantier est très rentable et peut absorber
                    des imprévus sans risque.
                  </p>
                </div>

                <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🟡</span>
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400">Bon</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">25-35%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 ml-7">
                    Bonne rentabilité. Continuez comme ça, le chantier est sur la bonne voie.
                  </p>
                </div>

                <div className="p-2 rounded bg-orange-500/10 border border-orange-500/20">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🟠</span>
                    <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">Attention</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">10-25%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 ml-7">
                    Marge faible. Surveillez les coûts et évitez les dépassements.
                    Un imprévu pourrait rendre le chantier déficitaire.
                  </p>
                </div>

                <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔴</span>
                    <span className="text-xs font-semibold text-red-600 dark:text-red-400">Critique</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">&lt;10%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 ml-7">
                    Danger ! La marge est quasi nulle ou le chantier est déficitaire.
                    Action immédiate requise.
                  </p>
                </div>
              </div>

              <div className="p-2 rounded-md bg-blue-500/10 border border-blue-500/20">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">💡 Où voir le badge ?</p>
                <p className="text-[10px] text-blue-600/80 dark:text-blue-400/80">
                  Sur la carte de chaque chantier (Dashboard et page Chantiers),
                  et dans l'en-tête de la fiche détaillée du chantier.
                </p>
              </div>
            </div>
          ),
        },
        {
          id: 'jour-critique',
          title: 'Le jour critique',
          icon: <AlertTriangle className="h-4 w-4 text-red-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Le <strong>jour critique</strong> est la date à laquelle votre marge atteindra 0€
                si les travaux continuent au rythme actuel. C'est votre date limite pour
                rester rentable.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment c'est calculé ?</p>
                <div className="p-2.5 rounded-lg bg-muted/50 border border-border/50">
                  <p className="text-xs font-mono text-center text-foreground mb-2">
                    Jour critique = Aujourd'hui + Jours rentables restants
                  </p>
                  <p className="text-[10px] text-muted-foreground text-center">
                    Basé sur le budget restant et le coût journalier de l'équipe
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Exemple</p>
                <div className="p-2 rounded bg-muted/30">
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">Aujourd'hui</span><span className="text-foreground">15 janvier</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Jours rentables</span><span className="text-foreground">10 jours</span></div>
                    <div className="flex justify-between border-t border-border/50 pt-1 mt-1">
                      <span className="font-semibold text-red-500">Jour critique</span>
                      <span className="font-bold text-red-500">25 janvier</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-red-500/10 border border-red-500/20">
                <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">⚠️ Si vous dépassez cette date</p>
                <ul className="text-[10px] text-red-600/80 dark:text-red-400/80 space-y-0.5">
                  <li>• Chaque jour supplémentaire = perte financière</li>
                  <li>• Le badge passe en rouge "Critique"</li>
                  <li>• Une alerte apparaît dans le Centre de Pilotage</li>
                </ul>
              </div>

              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">💡 Que faire si le jour critique approche ?</p>
                <ul className="text-[10px] text-amber-600/80 dark:text-amber-400/80 space-y-0.5">
                  <li>• Vérifiez si des factures peuvent être envoyées</li>
                  <li>• Réduisez l'équipe si possible</li>
                  <li>• Identifiez les causes de dépassement</li>
                  <li>• Lancez une analyse IA pour des recommandations</li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          id: 'jours-rentables',
          title: 'Les jours restants rentables',
          icon: <Timer className="h-4 w-4 text-blue-400" />,
          content: (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-muted/50 border border-border/50">
                <p className="text-sm font-mono text-center text-foreground">Budget dispo ÷ Coût/jour équipe</p>
              </div>
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 rounded bg-muted/30"><span className="text-muted-foreground">Budget:</span><span className="font-medium text-foreground ml-1">4 500€</span></div>
                  <div className="p-2 rounded bg-muted/30"><span className="text-muted-foreground">Coût/j:</span><span className="font-medium text-foreground ml-1">450€</span></div>
                </div>
                <div className="p-2 rounded bg-primary/10 border border-primary/20 text-center">
                  <span className="text-xs text-muted-foreground">= </span>
                  <span className="text-sm font-semibold text-primary">10 jours</span>
                </div>
              </div>
            </div>
          ),
        },
        {
          id: 'cout-journalier-equipe',
          title: 'Le coût journalier équipe',
          icon: <DollarSign className="h-4 w-4 text-emerald-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Somme des coûts de tous les membres affectés.</p>
              <div className="p-3 rounded-lg bg-muted/50 border border-border/50 space-y-2">
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Jean</span><span className="font-medium text-foreground">208€/j</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Marie</span><span className="font-medium text-foreground">120€/j</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Pierre</span><span className="font-medium text-foreground">280€/j</span></div>
                <div className="border-t border-border/50 pt-2 flex justify-between text-xs">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="font-bold text-primary">608€/j</span>
                </div>
              </div>
            </div>
          ),
        },
        {
          id: 'quand-inquieter',
          title: 'Quand s\'inquiéter ?',
          icon: <AlertTriangle className="h-4 w-4 text-orange-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Surveillez ces indicateurs pour détecter les problèmes avant qu'ils ne deviennent
                critiques. Plus vous agissez tôt, plus vous pouvez redresser la situation.
              </p>

              <div className="space-y-2">
                <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm">🚨</span>
                    <span className="text-xs font-bold text-red-600 dark:text-red-400">CRITIQUE : Marge &lt;10%</span>
                  </div>
                  <p className="text-[10px] text-red-600/80 dark:text-red-400/80 mb-1.5">
                    Le chantier est en danger immédiat de perte financière.
                  </p>
                  <div className="text-[10px] text-red-600 dark:text-red-400 font-medium">
                    → Actions : Arrêter les dépassements, facturer immédiatement, réduire l'équipe
                  </div>
                </div>

                <div className="p-2.5 rounded-lg bg-orange-500/10 border border-orange-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm">⚠️</span>
                    <span className="text-xs font-bold text-orange-600 dark:text-orange-400">ATTENTION : Marge 10-25%</span>
                  </div>
                  <p className="text-[10px] text-orange-600/80 dark:text-orange-400/80 mb-1.5">
                    La marge est fragile, un imprévu pourrait la faire basculer.
                  </p>
                  <div className="text-[10px] text-orange-600 dark:text-orange-400 font-medium">
                    → Actions : Surveiller quotidiennement, anticiper les problèmes, éviter les extras
                  </div>
                </div>

                <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm">📊</span>
                    <span className="text-xs font-bold text-amber-600 dark:text-amber-400">SURVEILLER : Écart &gt;5 points</span>
                  </div>
                  <p className="text-[10px] text-amber-600/80 dark:text-amber-400/80 mb-1.5">
                    La rentabilité s'éloigne des prévisions initiales (en + ou en -).
                  </p>
                  <div className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                    → Actions : Analyser les causes de l'écart, ajuster les prévisions si nécessaire
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-blue-500/10 border border-blue-500/20">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">💡 Conseil pro</p>
                <p className="text-[10px] text-blue-600/80 dark:text-blue-400/80">
                  Consultez le <strong>Centre de Pilotage</strong> chaque matin.
                  Toutes les alertes y sont centralisées avec des actions rapides.
                </p>
              </div>
            </div>
          ),
        },
      ],
    },

    // SECTION 4 : PAIEMENT CLIENT
    {
      id: 'paiement-client',
      title: 'Paiement Client',
      icon: <DollarSign className="h-5 w-5" />,
      iconColor: 'text-emerald-500',
      items: [
        {
          id: 'kpis-paiement',
          title: 'Les KPIs de paiement',
          icon: <BarChart3 className="h-4 w-4 text-emerald-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                L'onglet Paiement affiche <strong>3 KPIs essentiels</strong> pour suivre
                l'encaissement de votre chantier en temps réel.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Les 3 indicateurs clés</p>
                <div className="space-y-1.5">
                  <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0"></span>
                      <div>
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Total Devis HT</span>
                        <p className="text-[10px] text-muted-foreground">
                          Somme des devis actifs acceptés + avenants - remises
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0"></span>
                      <div>
                        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Total Encaissé</span>
                        <p className="text-[10px] text-muted-foreground">
                          Somme de tous les paiements avec statut "Encaissé"
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-2 rounded bg-orange-500/10 border border-orange-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                      <div>
                        <span className="text-xs font-medium text-orange-600 dark:text-orange-400">Reste à Encaisser</span>
                        <p className="text-[10px] text-muted-foreground">
                          Différence entre le total devis et le total encaissé
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mb-1">💡 Astuce</p>
                <p className="text-[10px] text-emerald-600/80 dark:text-emerald-400/80">
                  Un badge de statut s'affiche automatiquement en fonction du pourcentage encaissé.
                  Pas besoin de le calculer vous-même !
                </p>
              </div>
            </div>
          ),
        },
        {
          id: 'statuts-paiement',
          title: 'Les statuts de paiement',
          icon: <CheckSquare className="h-4 w-4 text-emerald-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Le statut de paiement est <strong>calculé automatiquement</strong> par B8ild
                en fonction du pourcentage encaissé par rapport au total à encaisser.
              </p>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Les 5 statuts possibles</p>

                <div className="p-2 rounded bg-slate-500/10 border border-slate-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Non facturé</span>
                    <span className="text-[10px] text-muted-foreground">Aucun devis</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Le chantier n'a pas encore de devis actif accepté.
                  </p>
                </div>

                <div className="p-2 rounded bg-gray-500/10 border border-gray-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">En attente</span>
                    <span className="text-[10px] text-muted-foreground">0% encaissé</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Devis présent mais aucun paiement reçu.
                  </p>
                </div>

                <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">Acompte reçu</span>
                    <span className="text-[10px] text-muted-foreground">1% - 50%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Un premier paiement a été reçu (jusqu'à 50% du total).
                  </p>
                </div>

                <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Partiellement payé</span>
                    <span className="text-[10px] text-muted-foreground">51% - 99%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Plus de la moitié est encaissée, solde en attente.
                  </p>
                </div>

                <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Payé intégralement</span>
                    <span className="text-[10px] text-muted-foreground">100%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Tout a été encaissé. Le chantier est réglé ! ✓
                  </p>
                </div>
              </div>
            </div>
          ),
        },
        {
          id: 'ajouter-paiement',
          title: 'Ajouter un paiement',
          icon: <DollarSign className="h-4 w-4 text-emerald-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Pour enregistrer un paiement client, cliquez sur <strong>"+ Ajouter"</strong> dans
                la section Paiements. Une modal s'ouvre avec deux options.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Onglet Manuel</p>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30">
                  <ul className="text-[10px] text-muted-foreground space-y-1.5">
                    <li>• <strong>Curseur de montant</strong> : Glissez pour sélectionner le montant (max = total devis)</li>
                    <li>• <strong>Suggestions rapides</strong> : Boutons 30%, 50%, 100% et "Reste" pour aller vite</li>
                    <li>• <strong>Saisie manuelle</strong> : Tapez directement le montant si besoin</li>
                    <li>• <strong>Type</strong> : Acompte, Solde ou Avance</li>
                    <li>• <strong>Moyen de paiement</strong> : Virement, Chèque, Espèces, CB, Prélèvement</li>
                    <li>• <strong>Facture liée</strong> : Associez le paiement à une facture client</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Onglet Scan IA</p>
                <div className="p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <p className="text-[10px] text-purple-600/80 dark:text-purple-400/80">
                    Uploadez un justificatif (relevé bancaire, reçu) et B8ild extrait
                    automatiquement le montant, la date et la référence via l'IA Claude Vision.
                  </p>
                </div>
              </div>

              <div className="p-2 rounded-md bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mb-1">💡 Conseil pro</p>
                <p className="text-[10px] text-emerald-600/80 dark:text-emerald-400/80">
                  Utilisez le bouton <strong>"Reste"</strong> pour enregistrer le solde final d'un seul clic.
                  Le montant exact restant à encaisser sera automatiquement rempli.
                </p>
              </div>
            </div>
          ),
        },
        {
          id: 'avenants-remises',
          title: 'Avenants et remises',
          icon: <Tags className="h-4 w-4 text-emerald-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Les <strong>avenants</strong> et <strong>remises</strong> permettent d'ajuster le montant
                total à encaisser sans modifier les devis originaux.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment ça marche ?</p>
                <div className="space-y-1.5">
                  <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">➕</span>
                      <div>
                        <span className="text-xs font-medium text-green-600 dark:text-green-400">Avenant</span>
                        <p className="text-[10px] text-muted-foreground">
                          Travaux supplémentaires → <strong>augmente</strong> le total à encaisser
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">➖</span>
                      <div>
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">Remise</span>
                        <p className="text-[10px] text-muted-foreground">
                          Geste commercial → <strong>réduit</strong> le total à encaisser
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30">
                <p className="text-[10px] font-semibold text-muted-foreground mb-1.5">Formule de calcul</p>
                <p className="text-xs font-mono bg-background/50 p-2 rounded">
                  Total = Devis + Avenants - Remises
                </p>
              </div>

              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">⚠️ Important</p>
                <p className="text-[10px] text-amber-600/80 dark:text-amber-400/80">
                  Les avenants et remises sont séparés des devis pour garder une traçabilité claire.
                  Le devis original reste intact pour référence.
                </p>
              </div>
            </div>
          ),
        },
        {
          id: 'scan-justificatif',
          title: 'Scanner un justificatif',
          icon: <RefreshCw className="h-4 w-4 text-purple-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                B8ild peut <strong>extraire automatiquement</strong> les informations d'un justificatif
                de paiement (relevé bancaire, reçu, virement) grâce au Scan IA.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Données extraites automatiquement</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20">
                    <span className="text-xs font-medium text-purple-600 dark:text-purple-400">💶 Montant</span>
                  </div>
                  <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20">
                    <span className="text-xs font-medium text-purple-600 dark:text-purple-400">📅 Date</span>
                  </div>
                  <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20">
                    <span className="text-xs font-medium text-purple-600 dark:text-purple-400">🔢 Référence</span>
                  </div>
                  <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20">
                    <span className="text-xs font-medium text-purple-600 dark:text-purple-400">💳 Type</span>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment utiliser ?</p>
                <ol className="text-[10px] text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Ouvrez la modal d'ajout de paiement</li>
                  <li>Cliquez sur l'onglet <strong>"Scan IA"</strong></li>
                  <li>Glissez-déposez votre fichier (PDF ou image)</li>
                  <li>Cliquez sur <strong>"Extraire"</strong></li>
                  <li>Vérifiez les données et cliquez sur <strong>"Utiliser ces données"</strong></li>
                </ol>
              </div>

              <div className="p-2 rounded-md bg-blue-500/10 border border-blue-500/20">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">💡 Formats supportés</p>
                <p className="text-[10px] text-blue-600/80 dark:text-blue-400/80">
                  PDF, JPEG, PNG. Assurez-vous que le document est lisible et non flou
                  pour une extraction optimale.
                </p>
              </div>
            </div>
          ),
        },
      ],
    },

    // SECTION 5 : ANALYSE IA
    {
      id: 'analyse-ia',
      title: 'Analyse IA',
      icon: <Brain className="h-5 w-5" />,
      iconColor: 'text-purple-500',
      items: [
        {
          id: 'score-global',
          title: 'Comprendre le score global',
          icon: <Target className="h-4 w-4 text-purple-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                L'analyse IA attribue un <strong>score de 0 à 100</strong> à votre chantier.
                Ce score reflète la santé globale du projet en combinant plusieurs critères.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Composition du score</p>
                <div className="space-y-1.5">
                  <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-blue-500">40%</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-blue-600 dark:text-blue-400">Santé financière</p>
                        <p className="text-[10px] text-muted-foreground">
                          Rentabilité, cash-flow, respect du budget, paiements
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-green-500">30%</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-green-600 dark:text-green-400">Planning</p>
                        <p className="text-[10px] text-muted-foreground">
                          Avancement vs prévisions, équipe affectée, risques de retard
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-purple-500">30%</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400">Complétude données</p>
                        <p className="text-[10px] text-muted-foreground">
                          Devis renseignés, factures saisies, équipe complète
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-purple-500/10 border border-purple-500/20">
                <p className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-1">💡 Comment améliorer le score ?</p>
                <ul className="text-[10px] text-purple-600/80 dark:text-purple-400/80 space-y-0.5">
                  <li>• Tenez vos données à jour (factures, frais, équipe)</li>
                  <li>• Respectez le planning initial autant que possible</li>
                  <li>• Suivez les recommandations de l'analyse IA</li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          id: 'etoiles',
          title: 'Lire les étoiles',
          icon: <Star className="h-4 w-4 text-amber-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Le score est traduit en étoiles pour une lecture rapide.
                Les étoiles s'affichent sur la fiche du chantier après une analyse IA.
              </p>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Correspondance score → étoiles</p>

                <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-amber-400 text-sm">⭐⭐⭐⭐⭐</span>
                    <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">90-100</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Excellent ! Le chantier est parfaitement géré.
                  </p>
                </div>

                <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-amber-400 text-sm">⭐⭐⭐⭐</span>
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400">75-89</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Très bien. Quelques ajustements mineurs possibles.
                  </p>
                </div>

                <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-amber-400 text-sm">⭐⭐⭐</span>
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">50-74</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Correct. Des améliorations sont recommandées.
                  </p>
                </div>

                <div className="p-2 rounded bg-orange-500/10 border border-orange-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-amber-400 text-sm">⭐⭐</span>
                    <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">25-49</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Attention requise. Consultez les recommandations IA.
                  </p>
                </div>

                <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-amber-400 text-sm">⭐</span>
                    <span className="text-xs font-semibold text-red-600 dark:text-red-400">0-24</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Situation critique. Action immédiate nécessaire.
                  </p>
                </div>
              </div>

              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">💡 Astuce</p>
                <p className="text-[10px] text-amber-600/80 dark:text-amber-400/80">
                  Visez au minimum 3 étoiles (score 50+). En dessous, le chantier présente
                  des risques significatifs qui nécessitent votre attention.
                </p>
              </div>
            </div>
          ),
        },
        {
          id: 'cooldown',
          title: 'Le cooldown de 6h',
          icon: <Timer className="h-4 w-4 text-blue-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Après avoir lancé une analyse IA sur un chantier, vous devez attendre
                <strong> 6 heures</strong> avant de pouvoir relancer une nouvelle analyse
                sur ce même chantier.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Pourquoi ce délai ?</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 p-2 rounded bg-muted/30">
                    <span className="text-sm">📊</span>
                    <div>
                      <p className="text-xs font-medium text-foreground">Données stables</p>
                      <p className="text-[10px] text-muted-foreground">
                        Les données d'un chantier évoluent peu en quelques heures.
                        Une analyse toutes les 6h est suffisante.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded bg-muted/30">
                    <span className="text-sm">💰</span>
                    <div>
                      <p className="text-xs font-medium text-foreground">Préserve votre quota</p>
                      <p className="text-[10px] text-muted-foreground">
                        Évite de consommer inutilement vos analyses mensuelles
                        sur le même chantier.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-blue-500/10 border border-blue-500/20">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">⏱️ Comment ça marche ?</p>
                <ul className="text-[10px] text-blue-600/80 dark:text-blue-400/80 space-y-0.5">
                  <li>• Analyse à 9h → prochaine possible à 15h</li>
                  <li>• Un compteur s'affiche sur le bouton si en cooldown</li>
                  <li>• Le cooldown est par chantier (pas global)</li>
                </ul>
              </div>

              <div className="p-2 rounded-md bg-green-500/10 border border-green-500/20">
                <p className="text-xs font-semibold text-green-600 dark:text-green-400 mb-1">🎁 Exceptions (gratuites et sans cooldown)</p>
                <ul className="text-[10px] text-green-600/80 dark:text-green-400/80 space-y-0.5">
                  <li>• <strong>Analyse de clôture</strong> : offerte quand vous terminez un chantier</li>
                  <li>• <strong>Première analyse</strong> : pas de cooldown pour un nouveau chantier</li>
                </ul>
              </div>
            </div>
          ),
        },
      ],
    },

    // SECTION 5 : RETARDS
    {
      id: 'retards',
      title: 'Retards',
      icon: <AlertTriangle className="h-5 w-5" />,
      iconColor: 'text-red-500',
      items: [
        {
          id: 'declarer-retard',
          title: 'Déclarer un retard',
          icon: <FileWarning className="h-4 w-4 text-red-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Si votre chantier ne pourra pas se terminer à la date prévue, déclarez
                officiellement le retard. Cela permet de recalculer l'impact financier
                et d'anticiper les pénalités éventuelles.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment déclarer ?</p>
                <ol className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">1</span>
                    <span>Ouvrez la fiche du chantier concerné</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">2</span>
                    <span>Allez dans l'onglet <strong>"Retard & Pénalités"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">3</span>
                    <span>Cliquez sur <strong>"Déclarer un retard"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">4</span>
                    <span>Renseignez la <strong>nouvelle date de fin</strong> prévue</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">5</span>
                    <span>Documentez le <strong>motif du retard</strong> (intempéries, problème client, etc.)</span>
                  </li>
                </ol>
              </div>

              <div className="p-2 rounded-md bg-red-500/10 border border-red-500/20">
                <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">⚠️ Conséquences d'un retard</p>
                <ul className="text-[10px] text-red-600/80 dark:text-red-400/80 space-y-0.5">
                  <li>• <strong>Coûts supplémentaires</strong> : l'équipe continue à travailler (et coûter)</li>
                  <li>• <strong>Pénalités contractuelles</strong> : si prévues dans le contrat</li>
                  <li>• <strong>Marge réduite</strong> : recalcul automatique de la rentabilité</li>
                </ul>
              </div>

              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">💡 Conseil</p>
                <p className="text-[10px] text-amber-600/80 dark:text-amber-400/80">
                  Utilisez le <strong>simulateur de pénalités</strong> AVANT de déclarer
                  pour évaluer différents scénarios de durée de retard.
                </p>
              </div>
            </div>
          ),
        },
        {
          id: 'simulateur-penalites',
          title: 'Le simulateur de pénalités',
          icon: <Calculator className="h-4 w-4 text-orange-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Le simulateur vous permet d'anticiper l'impact financier d'un retard
                <strong> avant qu'il ne se produise</strong>. Testez différents scénarios
                pour prendre les meilleures décisions.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment utiliser le simulateur ?</p>
                <ol className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">1</span>
                    <span>Allez dans <strong>Gestion Financière</strong> → onglet <strong>"Simulateur"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">2</span>
                    <span>Sélectionnez le <strong>chantier</strong> à analyser</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">3</span>
                    <span>Entrez le <strong>nombre de jours de retard</strong> envisagé</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">4</span>
                    <span>Ajoutez les <strong>pénalités contractuelles</strong> si applicable (€/jour ou % du marché)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">5</span>
                    <span>Consultez le <strong>résumé de l'impact</strong></span>
                  </li>
                </ol>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Ce que le simulateur calcule</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="p-1.5 rounded bg-muted/30">
                    <span className="text-xs font-medium text-foreground">💰 Coût équipe</span>
                    <p className="text-[10px] text-muted-foreground">Salaires sur jours supplémentaires</p>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <span className="text-xs font-medium text-foreground">⚖️ Pénalités</span>
                    <p className="text-[10px] text-muted-foreground">Selon clauses du contrat</p>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <span className="text-xs font-medium text-foreground">📉 Marge finale</span>
                    <p className="text-[10px] text-muted-foreground">Rentabilité après retard</p>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <span className="text-xs font-medium text-foreground">📊 Comparaison</span>
                    <p className="text-[10px] text-muted-foreground">Avant vs après retard</p>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">💡 Utilisation recommandée</p>
                <ul className="text-[10px] text-amber-600/80 dark:text-amber-400/80 space-y-0.5">
                  <li>• Testez plusieurs scénarios (5j, 10j, 15j de retard)</li>
                  <li>• Comparez les options : finir en retard vs réduire l'équipe</li>
                  <li>• Utilisez les résultats pour négocier avec le client</li>
                </ul>
              </div>
            </div>
          ),
          action: { label: 'Ouvrir le simulateur', onClick: () => { onCollapse(); navigate('/financial?tab=penalty-simulator'); } },
        },
        {
          id: 'affectations-retard',
          title: 'Les affectations "Retard chantier"',
          icon: <Users className="h-4 w-4 text-red-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Quand un chantier dépasse sa date de fin prévue, les affectations des
                membres de l'équipe passent automatiquement en statut <strong>"Retard chantier"</strong>.
                C'est un indicateur visuel important dans B8ild.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Ce qui se passe automatiquement</p>
                <div className="space-y-1.5">
                  <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"></span>
                      <div>
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">Badge "Retard"</span>
                        <p className="text-[10px] text-muted-foreground">
                          L'affectation s'affiche en rouge dans le planning et la fiche chantier
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-2 rounded bg-orange-500/10 border border-orange-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                      <div>
                        <span className="text-xs font-medium text-orange-600 dark:text-orange-400">Coût qui s'accumule</span>
                        <p className="text-[10px] text-muted-foreground">
                          Les jours supplémentaires sont comptabilisés dans les dépenses
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500 flex-shrink-0"></span>
                      <div>
                        <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Alerte dans le Pilotage</span>
                        <p className="text-[10px] text-muted-foreground">
                          Le chantier apparaît dans les alertes "Retard" du Centre de Pilotage
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-blue-500/10 border border-blue-500/20">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">💡 Comment gérer les affectations en retard ?</p>
                <ul className="text-[10px] text-blue-600/80 dark:text-blue-400/80 space-y-0.5">
                  <li>• <strong>Option 1</strong> : Prolonger la date de fin du chantier (déclaration de retard)</li>
                  <li>• <strong>Option 2</strong> : Retirer des membres de l'équipe pour réduire les coûts</li>
                  <li>• <strong>Option 3</strong> : Terminer le chantier (passage en "Terminé")</li>
                </ul>
              </div>

              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">⚠️ Impact financier</p>
                <p className="text-[10px] text-amber-600/80 dark:text-amber-400/80">
                  Chaque jour de retard = coût journalier de l'équipe qui s'ajoute.
                  Pour une équipe à 600€/jour, 10 jours de retard = 6 000€ de coûts supplémentaires.
                </p>
              </div>
            </div>
          ),
        },
      ],
    },

    // SECTION 6 : COÛTS & SOUS-TRAITANCE
    {
      id: 'couts-sous-traitance',
      title: 'Coûts & Sous-traitance',
      icon: <DollarSign className="h-5 w-5" />,
      iconColor: 'text-cyan-500',
      items: [
        {
          id: 'types-frais',
          title: 'Les types de frais',
          icon: <Tags className="h-4 w-4 text-cyan-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                L'onglet <strong>Coûts annexes</strong> permet d'enregistrer toutes les dépenses
                liées à un chantier, en dehors des factures fournisseurs classiques.
              </p>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Types de frais disponibles</p>
                <div className="space-y-1">
                  <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20">
                    <span className="text-xs font-medium text-purple-600 dark:text-purple-400">🏠 Matériaux</span>
                    <p className="text-[10px] text-muted-foreground">Achats de matériaux hors factures fournisseurs</p>
                  </div>
                  <div className="p-2 rounded bg-cyan-500/10 border border-cyan-500/20">
                    <span className="text-xs font-medium text-cyan-600 dark:text-cyan-400">👷 Sous-traitant</span>
                    <p className="text-[10px] text-muted-foreground">Prestations d'entreprises externes (électricien, plombier...)</p>
                  </div>
                  <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">🚗 Déplacement</span>
                    <p className="text-[10px] text-muted-foreground">Frais kilométriques, péages, parking</p>
                  </div>
                  <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
                    <span className="text-xs font-medium text-green-600 dark:text-green-400">🏨 Hébergement</span>
                    <p className="text-[10px] text-muted-foreground">Hôtel, location appartement pour chantiers lointains</p>
                  </div>
                  <div className="p-2 rounded bg-orange-500/10 border border-orange-500/20">
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">🔧 Location matériel</span>
                    <p className="text-[10px] text-muted-foreground">Location d'engins, échafaudages, outils</p>
                  </div>
                  <div className="p-2 rounded bg-gray-500/10 border border-gray-500/20">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">📋 Autre</span>
                    <p className="text-[10px] text-muted-foreground">Tout autre type de frais</p>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-xs font-medium text-cyan-600 dark:text-cyan-400 mb-1">💡 Astuce</p>
                <p className="text-[10px] text-cyan-600/80 dark:text-cyan-400/80">
                  Le type <strong>"Sous-traitant"</strong> est spécial : il permet de lier les frais
                  à un contrat et de suivre l'avancement prévu vs facturé.
                </p>
              </div>
            </div>
          ),
        },
        {
          id: 'sous-traitant-definition',
          title: 'Qu\'est-ce qu\'un sous-traitant ?',
          icon: <Users className="h-4 w-4" style={{ color: '#06b6d4' }} />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Un <strong>sous-traitant</strong> est une entreprise externe à qui vous confiez
                une partie des travaux d'un chantier. Contrairement aux fournisseurs (qui livrent
                des matériaux), les sous-traitants réalisent des prestations de main d'œuvre.
              </p>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Exemples courants</p>
                <div className="grid grid-cols-2 gap-1">
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">⚡ Électricien</div>
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">🔧 Plombier</div>
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">🧱 Plaquiste</div>
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">🎨 Peintre</div>
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">🪟 Menuisier</div>
                  <div className="p-1.5 rounded bg-muted/30 text-[10px] text-muted-foreground">❄️ Climaticien</div>
                </div>
              </div>

              <div className="p-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 mb-1">🎯 Avantages dans B8ild</p>
                <ul className="text-[10px] text-cyan-600/80 dark:text-cyan-400/80 space-y-0.5">
                  <li>• <strong>Carnet d'adresses</strong> : réutilisez vos ST sur tous vos chantiers</li>
                  <li>• <strong>Suivi prévu vs facturé</strong> : visualisez l'avancement des paiements</li>
                  <li>• <strong>Historique</strong> : gardez trace de toutes les factures par contrat</li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          id: 'creer-sous-traitant',
          title: 'Créer un sous-traitant',
          icon: <Users className="h-4 w-4 text-cyan-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Le <strong>carnet d'adresses</strong> des sous-traitants est partagé entre tous vos
                chantiers. Créez un ST une fois, réutilisez-le partout.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment créer un sous-traitant ?</p>
                <ol className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">1</span>
                    <span>Ouvrez un chantier → onglet <strong>"Coûts annexes"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">2</span>
                    <span>Cliquez sur <strong>"+ Ajouter un frais"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">3</span>
                    <span>Sélectionnez le type <strong>"Sous-traitant"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">4</span>
                    <span>Cliquez sur <strong>"+ Créer un sous-traitant"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">5</span>
                    <span>Renseignez le <strong>nom</strong> et la <strong>spécialité</strong></span>
                  </li>
                </ol>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Informations du sous-traitant</p>
                <div className="grid grid-cols-2 gap-1">
                  <div className="p-1.5 rounded bg-cyan-500/10 border border-cyan-500/20">
                    <span className="text-[10px] font-medium text-cyan-600 dark:text-cyan-400">Nom *</span>
                  </div>
                  <div className="p-1.5 rounded bg-cyan-500/10 border border-cyan-500/20">
                    <span className="text-[10px] font-medium text-cyan-600 dark:text-cyan-400">Spécialité</span>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <span className="text-[10px] text-muted-foreground">Téléphone</span>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <span className="text-[10px] text-muted-foreground">Email</span>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <span className="text-[10px] text-muted-foreground">SIRET</span>
                  </div>
                  <div className="p-1.5 rounded bg-muted/30">
                    <span className="text-[10px] text-muted-foreground">Adresse</span>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground italic">* Obligatoire</p>
              </div>
            </div>
          ),
        },
        {
          id: 'contrat-sous-traitance',
          title: 'Créer un contrat ST',
          icon: <Tags className="h-4 w-4 text-cyan-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Chaque sous-traitant intervenant sur un chantier doit avoir un <strong>contrat</strong>
                qui définit le montant prévu et le périmètre des travaux.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment créer un contrat ?</p>
                <ol className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">1</span>
                    <span>Sélectionnez un <strong>sous-traitant</strong> dans la liste</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">2</span>
                    <span>Cliquez sur <strong>"+ Créer un contrat"</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">3</span>
                    <span>Renseignez le <strong>montant prévu HT</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-500 text-[10px] flex items-center justify-center font-medium flex-shrink-0">4</span>
                    <span>Ajoutez une <strong>description des travaux</strong></span>
                  </li>
                </ol>
              </div>

              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">⚠️ Règle importante</p>
                <p className="text-[10px] text-amber-600/80 dark:text-amber-400/80">
                  Un sous-traitant ne peut avoir qu'<strong>un seul contrat par chantier</strong>.
                  Si plusieurs périmètres, regroupez-les dans un seul contrat avec description détaillée.
                </p>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Statuts du contrat</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 p-1.5 rounded bg-blue-500/10">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span className="text-xs text-blue-600 dark:text-blue-400">À démarrer</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Signé, travaux pas commencés</span>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-yellow-500/10">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                    <span className="text-xs text-yellow-600 dark:text-yellow-400">En cours</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Travaux en exécution</span>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-green-500/10">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="text-xs text-green-600 dark:text-green-400">Terminé</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Travaux finis et réceptionnés</span>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-gray-500/10">
                    <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Annulé</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Contrat annulé</span>
                  </div>
                </div>
              </div>
            </div>
          ),
        },
        {
          id: 'suivi-prevu-facture',
          title: 'Suivi prévu vs facturé',
          icon: <BarChart3 className="h-4 w-4 text-cyan-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Le cœur de la gestion sous-traitant : comparez le <strong>montant prévu</strong>
                au contrat avec le <strong>montant réellement facturé</strong>.
              </p>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Les 4 indicateurs</p>
                <div className="space-y-1">
                  <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Montant prévu</span>
                      <span className="text-[10px] text-muted-foreground">= Contrat HT</span>
                    </div>
                  </div>
                  <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-green-600 dark:text-green-400">Montant facturé</span>
                      <span className="text-[10px] text-muted-foreground">= Somme des frais liés</span>
                    </div>
                  </div>
                  <div className="p-2 rounded bg-orange-500/10 border border-orange-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">Reste à facturer</span>
                      <span className="text-[10px] text-muted-foreground">= Prévu - Facturé</span>
                    </div>
                  </div>
                  <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-purple-600 dark:text-purple-400">% facturé</span>
                      <span className="text-[10px] text-muted-foreground">= (Facturé / Prévu) × 100</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Exemple</p>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30 space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Contrat électricien</span><span className="font-medium text-foreground">8 000 € HT</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Acompte versé</span><span className="text-green-500">2 400 € (30%)</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Facture intermédiaire</span><span className="text-green-500">3 200 € (40%)</span></div>
                  <div className="border-t border-border/50 pt-1 mt-1 flex justify-between">
                    <span className="font-semibold text-foreground">Total facturé</span>
                    <span className="font-bold text-green-500">5 600 € (70%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-orange-500">Reste</span>
                    <span className="font-bold text-orange-500">2 400 € (30%)</span>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-xs font-medium text-cyan-600 dark:text-cyan-400 mb-1">📊 Barres de progression</p>
                <p className="text-[10px] text-cyan-600/80 dark:text-cyan-400/80">
                  Chaque contrat affiche une barre visuelle. <span className="text-green-400">Vert</span> = normal,
                  <span className="text-yellow-400"> Jaune</span> = 80%+, <span className="text-red-400">Rouge</span> = dépassement.
                </p>
              </div>
            </div>
          ),
        },
        {
          id: 'types-factures-st',
          title: 'Types de factures ST',
          icon: <Tags className="h-4 w-4 text-cyan-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Lors de l'enregistrement d'une facture de sous-traitant, indiquez son <strong>type</strong>
                pour un meilleur suivi.
              </p>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Les 3 types de factures</p>
                <div className="space-y-1.5">
                  <div className="p-2.5 rounded bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm">💰</span>
                      <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">Acompte</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Avance versée avant ou en début de travaux (généralement 30-40% du contrat).
                    </p>
                  </div>
                  <div className="p-2.5 rounded bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm">📄</span>
                      <span className="text-xs font-semibold text-green-600 dark:text-green-400">Facture</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Facture intermédiaire pendant les travaux. Pour les chantiers longs (&gt;1 mois).
                    </p>
                  </div>
                  <div className="p-2.5 rounded bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm">✅</span>
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Solde</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Facture finale de clôture, après vérification et réception des travaux.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">💡 Bonnes pratiques</p>
                <ul className="text-[10px] text-amber-600/80 dark:text-amber-400/80 space-y-0.5">
                  <li>• Commencez par un <strong>acompte</strong> pour sécuriser le ST</li>
                  <li>• Utilisez des <strong>factures</strong> intermédiaires sur les gros chantiers</li>
                  <li>• Terminez par le <strong>solde</strong> après vérification des travaux</li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          id: 'recap-contrats-st',
          title: 'Le récapitulatif contrats ST',
          icon: <BarChart3 className="h-4 w-4" style={{ color: '#06b6d4' }} />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                En haut de l'onglet <strong>Coûts annexes</strong>, une section dépliable affiche
                le récapitulatif de tous vos contrats sous-traitants.
              </p>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Ce qui est affiché</p>
                <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30 space-y-2">
                  <div className="text-xs font-semibold text-foreground">📋 Totaux globaux</div>
                  <div className="grid grid-cols-3 gap-1 text-[10px]">
                    <div className="p-1.5 rounded bg-blue-500/10 text-center">
                      <span className="text-blue-600 dark:text-blue-400">Prévu</span>
                    </div>
                    <div className="p-1.5 rounded bg-green-500/10 text-center">
                      <span className="text-green-600 dark:text-green-400">Facturé</span>
                    </div>
                    <div className="p-1.5 rounded bg-orange-500/10 text-center">
                      <span className="text-orange-600 dark:text-orange-400">Reste</span>
                    </div>
                  </div>
                  <div className="text-xs font-semibold text-foreground mt-2">📑 Détail par contrat</div>
                  <ul className="text-[10px] text-muted-foreground space-y-0.5">
                    <li>• Nom du ST + badge spécialité</li>
                    <li>• Description des travaux</li>
                    <li>• Barre de progression</li>
                    <li>• Nombre de factures</li>
                    <li>• Historique des factures (dépliable)</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Actions disponibles</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 p-1.5 rounded bg-green-500/10">
                    <span className="text-green-400">+</span>
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">Ajouter facture</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Ouvre le modal pré-rempli</span>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-red-500/10">
                    <span className="text-red-400">🗑️</span>
                    <span className="text-xs text-red-600 dark:text-red-400 font-medium">Supprimer contrat</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Si aucune facture liée</span>
                  </div>
                </div>
              </div>
            </div>
          ),
        },
      ],
    },

    // SECTION 7 : ASTUCES PRO
    {
      id: 'astuces-pro',
      title: 'Astuces Pro',
      icon: <Lightbulb className="h-5 w-5" />,
      iconColor: 'text-yellow-500',
      items: [
        {
          id: 'benchmarks-btp',
          title: 'Benchmarks BTP par spécialité',
          icon: <BarChart3 className="h-4 w-4 text-yellow-400" />,
          content: (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Ces chiffres sont issus des études de la <strong>Fédération Française du Bâtiment (FFB)</strong>.
                Utilisez-les pour comparer vos marges avec la moyenne du secteur et
                identifier des opportunités d'amélioration.
              </p>

              <div className="space-y-2">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Marges de référence par spécialité</p>
                {[
                  { nom: '🧱 Carrelage', min: '18%', cible: '25%', excel: '35%' },
                  { nom: '🔧 Plomberie', min: '20%', cible: '28%', excel: '40%' },
                  { nom: '⚡ Électricité', min: '18%', cible: '25%', excel: '35%' },
                  { nom: '🏗️ Gros œuvre', min: '15%', cible: '22%', excel: '30%' },
                  { nom: '🔨 Rénovation', min: '20%', cible: '30%', excel: '45%' },
                ].map((item) => (
                  <div key={item.nom} className="p-2 rounded-lg bg-muted/30 border border-border/30">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-foreground">{item.nom}</span>
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>Min: <span className="text-orange-400 font-medium">{item.min}</span></span>
                      <span>Cible: <span className="text-green-400 font-medium">{item.cible}</span></span>
                      <span>Excel: <span className="text-emerald-400 font-medium">{item.excel}</span></span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">Comment lire ces chiffres ?</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 p-1.5 rounded bg-orange-500/10">
                    <span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0"></span>
                    <span className="text-xs text-orange-600 dark:text-orange-400"><strong>Min</strong></span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Seuil de rentabilité minimal</span>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-green-500/10">
                    <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0"></span>
                    <span className="text-xs text-green-600 dark:text-green-400"><strong>Cible</strong></span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Objectif à viser</span>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-emerald-500/10">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0"></span>
                    <span className="text-xs text-emerald-600 dark:text-emerald-400"><strong>Excel</strong></span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Performance excellente</span>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded-md bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-xs font-medium text-yellow-600 dark:text-yellow-400 mb-1">💡 Comment utiliser ces benchmarks ?</p>
                <ul className="text-[10px] text-yellow-600/80 dark:text-yellow-400/80 space-y-0.5">
                  <li>• Comparez vos marges réelles avec ces références</li>
                  <li>• Si vous êtes sous le "Min", analysez vos devis</li>
                  <li>• Ajustez vos prix pour atteindre la "Cible"</li>
                  <li>• Les marges varient selon la région et la concurrence</li>
                </ul>
              </div>
            </div>
          ),
        },
      ],
    },
  ], [navigate, onCollapse, openPlanning, openPilotage, setActiveTab, expandedStatut]);

  if (!isVisible) return null;

  const HIGHLIGHT_TARGETS: Record<string, string> = {
    'equipe': 'create-team',
    'chantier': 'create-project',
    'devis': 'add-devis',
    'affectation': 'add-affectation',
    'factures': 'add-facture',
    'couts-annexes': 'add-frais',
    'budget-ventile': 'budget-ventile',
    'paiement': 'add-paiement',
    'simulation-retard': 'simulation-retard',
    'analyse-ia': 'launch-analysis',
  };

  const CHANTIER_DETAIL_STEPS = ['devis', 'affectation', 'factures', 'couts-annexes', 'budget-ventile', 'paiement', 'simulation-retard', 'analyse-ia'];

  const handleStepClick = async (item: ChecklistItem) => {
    if (item.action && !item.isCompleted && !isValidatingChantier) {
      const highlightTarget = HIGHLIGHT_TARGETS[item.id];

      if (CHANTIER_DETAIL_STEPS.includes(item.id)) {
        setIsValidatingChantier(true);
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            onCollapse();
            navigate('/auth');
            setIsValidatingChantier(false);
            return;
          }

          const { data: entreprise } = await supabase
            .from('entreprises')
            .select('id')
            .eq('proprietaire_user_id', user.id)
            .maybeSingle();

          let chantierId = lastCreatedChantierId;

          const findValidChantier = async () => {
            const query = supabase.from('chantiers').select('id').order('created_at', { ascending: true }).limit(1);
            if (entreprise?.id) query.eq('entreprise_id', entreprise.id);
            const { data } = await query.maybeSingle();
            return data;
          };

          if (!chantierId) {
            const firstChantier = await findValidChantier();
            if (firstChantier) {
              chantierId = firstChantier.id;
              setLastCreatedChantierId(chantierId);
            } else {
              onCollapse();
              toast.info('Créez d\'abord un chantier pour continuer.');
              navigate('/projects?highlight=create-project', { state: { fromChecklist: true } });
              setIsValidatingChantier(false);
              return;
            }
          }

          const validateQuery = supabase.from('chantiers').select('id').eq('id', chantierId);
          if (entreprise?.id) validateQuery.eq('entreprise_id', entreprise.id);
          const { data: chantierExists, error: validateError } = await validateQuery.maybeSingle();

          if (!chantierExists || validateError) {
            setLastCreatedChantierId(null);
            const autreChantier = await findValidChantier();
            if (autreChantier) {
              chantierId = autreChantier.id;
              setLastCreatedChantierId(chantierId);
            } else {
              onCollapse();
              toast.info('Le chantier a été supprimé. Créez-en un nouveau pour continuer.');
              navigate('/projects?highlight=create-project', { state: { fromChecklist: true } });
              setIsValidatingChantier(false);
              return;
            }
          }

          onCollapse();
          if (item.id === 'factures') localStorage.setItem(`b8ild_invoices_tab_seen_${chantierId}`, 'true');
          if (item.id === 'simulation-retard') {
            navigate('/financial?tab=penalty-simulator', { state: { fromChecklist: true, chantierId } });
            setIsValidatingChantier(false);
            return;
          }
          if (item.id === 'budget-ventile') {
            navigate(`/projects/${chantierId}?showBudgetTip=true`, { state: { fromChecklist: true } });
            setIsValidatingChantier(false);
            return;
          }
          navigate(`/projects/${chantierId}?highlight=${highlightTarget}`, { state: { fromChecklist: true } });
        } catch (error) {
          console.error('[ChecklistPanel] Erreur validation chantier:', error);
          onCollapse();
          navigate('/projects?highlight=create-project', { state: { fromChecklist: true } });
        } finally {
          setIsValidatingChantier(false);
        }
      } else {
        onCollapse();
        if (item.id === 'simulation-retard') {
          navigate('/financial?tab=penalty-simulator', { state: { fromChecklist: true } });
          return;
        }
        const url = highlightTarget ? `${item.action}?highlight=${highlightTarget}` : item.action;
        navigate(url, { state: { fromChecklist: true } });
      }
    }
  };

  const handleHide = () => {
    if (isAllCompleted && onHideCompletely) {
      onHideCompletely();
    } else {
      onHideChecklist();
    }
    onCollapse();
  };

  return (
    <div className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', 'pointer-events-none')}>
      <div className={cn(
        'pointer-events-auto',
        'w-full max-w-lg',
        'bg-card/98 backdrop-blur-md',
        'border border-border/50 rounded-2xl',
        'shadow-2xl shadow-black/40',
        'flex flex-col',
        'max-h-[85vh]',
        'isolate overflow-hidden',
        'animate-in fade-in-0 zoom-in-95 duration-200'
      )}>
        {/* HEADER */}
        <div className="flex-shrink-0 p-5 pb-3 border-b border-border/50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={cn("p-2 rounded-lg", isAllCompleted ? "bg-blue-500/10" : "bg-primary/10")}>
                {isAllCompleted ? <BookOpen className="w-5 h-5 text-blue-500" /> : <Sparkles className="w-5 h-5 text-primary" />}
              </div>
              <div>
                <h2 className="font-bold text-foreground text-lg">{isAllCompleted ? "Guide d'utilisation" : "Premiers pas"}</h2>
                <p className="text-xs text-muted-foreground">
                  {isAllCompleted ? "Maîtrisez toutes les fonctionnalités" : `${completedCount} sur ${totalCount} complétés`}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onCollapse} className="h-8 w-8 text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </Button>
          </div>
          {!isAllCompleted && <Progress value={progressPercent} className="h-2" />}
        </div>

        {/* CONTENU */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          {isAllCompleted ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <PartyPopper className="w-5 h-5 text-green-500 flex-shrink-0" />
                <p className="text-sm text-green-700 dark:text-green-400">Bravo ! Premiers pas complétés.</p>
              </div>

              <Accordion type="single" collapsible value={expandedSection} onValueChange={setExpandedSection} className="space-y-2">
                {guideSections.map((section) => (
                  <AccordionItem key={section.id} value={section.id} className="border border-white/10 rounded-xl overflow-hidden bg-white/[0.02]">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-white/[0.03] transition-colors">
                      <div className="flex items-center gap-3 w-full">
                        <div className={cn("flex-shrink-0", section.iconColor)}>{section.icon}</div>
                        <span className="font-semibold text-foreground text-sm flex-1 text-left">{section.title}</span>
                        {section.badge && (
                          <Badge variant={section.badge.variant} className={cn("text-[9px] px-1.5 py-0", section.badge.className)}>
                            {section.badge.label}
                          </Badge>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3 bg-white/[0.02]">
                      <div className="space-y-1.5 pt-2">
                        {section.items.map((guideItem) => (
                          <div key={guideItem.id} className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.05]">
                            <button
                              onClick={() => setExpandedItem(expandedItem === guideItem.id ? null : guideItem.id)}
                              className="w-full flex items-center gap-2 p-2.5 hover:bg-white/[0.03] transition-colors"
                            >
                              <div className="flex-shrink-0">{guideItem.icon}</div>
                              <span className="text-xs font-medium text-foreground flex-1 text-left">{guideItem.title}</span>
                              <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", expandedItem === guideItem.id && "rotate-180")} />
                            </button>
                            {expandedItem === guideItem.id && (
                              <div className="px-3 pb-3 pt-1 border-t border-white/10">
                                {guideItem.content}
                                {guideItem.action && (
                                  <Button variant="outline" size="sm" className="mt-3 w-full text-xs" onClick={guideItem.action.onClick}>
                                    {guideItem.action.label}
                                    <ArrowRight className="ml-2 h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => {
                const isCompleted = item.isCompleted;
                const isNextStep = item.id === nextIncompleteItemId;
                const shouldBlink = isNextStep && isBlinking;
                const showShootingStar = item.id === shootingStarItemId;

                return (
                  <button
                    key={item.id}
                    onClick={() => handleStepClick(item)}
                    disabled={isCompleted}
                    className={cn(
                      'w-full flex items-center gap-3 p-3 rounded-xl text-left',
                      'transition-all duration-200',
                      isCompleted ? 'opacity-60 cursor-default bg-muted/30' : 'hover:bg-muted/50 cursor-pointer group bg-card border border-border/50',
                      shouldBlink && !showShootingStar && 'border-primary/50 animate-subtle-glow',
                      showShootingStar && 'relative overflow-hidden border-transparent'
                    )}
                  >
                    {/* Animation étoile filante autour de la bordure */}
                    {showShootingStar && (
                      <>
                        <span
                          className="absolute -inset-[2px] rounded-xl pointer-events-none overflow-hidden"
                          style={{
                            background: 'conic-gradient(from var(--angle), transparent 0%, transparent 75%, hsl(207 56% 45%) 80%, hsl(207 56% 60%) 85%, transparent 90%, transparent 100%)',
                            animation: 'borderRotate 1.5s linear forwards',
                          }}
                        />
                        {/* Masque intérieur pour ne garder que la bordure */}
                        <span
                          className="absolute inset-[1px] rounded-[11px] pointer-events-none bg-card"
                        />
                      </>
                    )}
                    <div className={cn("flex items-center gap-3 w-full", showShootingStar && "relative z-10")}>
                      <div className="flex-shrink-0">
                        {isCompleted ? <CheckCircle2 className="h-6 w-6 text-green-500" /> : <Circle className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors" />}
                      </div>
                      <span className="text-2xl flex-shrink-0">{item.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-sm font-semibold', isCompleted ? 'line-through text-muted-foreground' : 'text-foreground')}>{item.label}</p>
                        <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                      </div>
                      {!isCompleted && <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 p-4 pt-3 border-t border-border/50 space-y-2">
          <button onClick={handleHide} className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-2">
            {isAllCompleted ? "Masquer ce guide" : "Je connais déjà B8ild, masquer cette section"}
          </button>
          {onResetChecklist && (
            <button onClick={onResetChecklist} className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors py-1">
              <RotateCcw className="h-3 w-3" />
              Réinitialiser la checklist (test)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistPanelExpanded;
